import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "uc_access_token";
const REFRESH_KEY = "uc_refresh_token";

export const tokenStorage = {
  get: () => {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  },
  getRefresh: () => {
    try {
      return localStorage.getItem(REFRESH_KEY);
    } catch {
      return null;
    }
  },
  set: (access, refresh) => {
    try {
      if (access) localStorage.setItem(TOKEN_KEY, access);
      if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
    } catch {
      /* ignore quota / privacy mode */
    }
  },
  clear: () => {
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_KEY);
    } catch {
      /* ignore */
    }
  },
};

export const api = axios.create({
  baseURL: API,
  withCredentials: true, // keep cookie auth for desktop browsers that allow it
});

// Attach Authorization header from localStorage (mobile-friendly fallback
// for browsers that block cross-site cookies, e.g. iOS Safari ITP).
api.interceptors.request.use((config) => {
  const token = tokenStorage.get();
  if (token) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// Auto-clear stale tokens on 401 (so the next /auth/me lands on guest)
api.interceptors.response.use(
  (r) => {
    // AUTO-INVALIDATE CACHE on any successful mutating request. We check
    // config here (not r.data) because the response can be anything (even
    // empty 204). This covers DeviceDialog/ScaffoldDialog/inspections/revisions
    // etc. without needing them to know about the cache.
    try {
      const method = (r.config?.method || "get").toLowerCase();
      if (method !== "get" && method !== "head" && method !== "options") {
        const url = r.config?.url || "";
        // Lazy import to avoid circular deps (dataCache has no deps)
        // eslint-disable-next-line global-require
        const { invalidate } = require("./dataCache");
        if (/\/devices?(\/|$|\?)/.test(url) || /\/inspections?/.test(url)) {
          invalidate("devices:dpi");
          invalidate("devices:strumento");
          invalidate("dashboard:home");
        }
        if (/\/scaffolds?(\/|$|\?)/.test(url) || /\/revisions?/.test(url)) {
          invalidate("scaffolds:scaffalatura");
          invalidate("scaffolds:lineavita");
          invalidate("dashboard:home");
        }
        if (/\/clients?(\/|$|\?)/.test(url)) {
          invalidate("clients:all");
        }
      }
    } catch (_e) { /* best effort */ }
    return r;
  },
  (err) => {
    if (err?.response?.status === 401) {
      const url = err?.config?.url || "";
      // Don't clear if it was the login attempt itself
      if (!url.includes("/auth/login")) {
        tokenStorage.clear();
      }
    }
    return Promise.reject(err);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Qualcosa è andato storto. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

/**
 * Authenticated fetch helper.
 * Includes both:
 *   - Authorization: Bearer header (preferred, works everywhere — mobile-safe)
 *   - cookies via credentials: "include" (kept for compatibility)
 * Use this instead of raw fetch() for any /api endpoint that needs auth.
 */
export function authedFetch(url, init = {}) {
  const token = tokenStorage.get();
  const headers = new Headers(init.headers || {});
  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  return fetch(url, {
    credentials: "include",
    ...init,
    headers,
  });
}
